<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useAxios } from 'dashboard/composables/useAxios';
import { useAccount } from 'dashboard/composables/useAccount';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import VoiceAgentCard from 'dashboard/components-next/aivoice/VoiceAgentCard.vue';
import VoiceAgentEmptyState from '../emptyStates/VoiceAgentEmptyState.vue';
import CreateVoiceAgentDialog from 'dashboard/components-next/aivoice/CreateVoiceAgentDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const router = useRouter();
const { accountScopedRoute } = useAccount();
const axios = useAxios();

const voiceAgents = ref([]);
const isFetching = ref(false);
const selectedAgent = ref(null);
const showDeleteDialog = ref(false);
const showCreateDialog = ref(false);
const dialogType = ref('');
const createVoiceAgentDialog = ref(null);

const fetchVoiceAgents = async () => {
  isFetching.value = true;
  try {
    const { data } = await axios.get(
      '/api/v1/accounts/:account_id/voice_agents'
    );
    voiceAgents.value = data;
  } catch (error) {
    console.error('Error fetching voice agents:', error);
  } finally {
    isFetching.value = false;
  }
};

const handleCreate = () => {
  selectedAgent.value = null;
  dialogType.value = 'create';
  showCreateDialog.value = true;
  nextTick(() => createVoiceAgentDialog.value.dialogRef.open());
};

const handleCreateClose = () => {
  showCreateDialog.value = false;
  dialogType.value = '';
  selectedAgent.value = null;
};

const handleVoiceAgentCreated = newAgent => {
  voiceAgents.value.unshift(newAgent);
};

const handleEdit = agent => {
  router.push({
    name: 'aivoice_voice_agents_edit',
    params: { voiceAgentId: agent.id },
  });
};

const handleDelete = agent => {
  selectedAgent.value = agent;
  showDeleteDialog.value = true;
};

const handleAction = ({ action, id }) => {
  const agent = voiceAgents.value.find(a => a.id === id);

  nextTick(() => {
    if (action === 'delete') {
      handleDelete(agent);
    } else if (action === 'edit') {
      handleEdit(agent);
    } else if (action === 'connectChannel') {
      // TODO: Implement connect channel dialog
      console.log('Connect channel for agent:', agent.id);
    }
  });
};

const confirmDelete = async () => {
  if (!selectedAgent.value) return;

  try {
    await axios.delete(
      `/api/v1/accounts/:account_id/voice_agents/${selectedAgent.value.id}`
    );
    voiceAgents.value = voiceAgents.value.filter(
      a => a.id !== selectedAgent.value.id
    );
    showDeleteDialog.value = false;
    selectedAgent.value = null;
  } catch (error) {
    console.error('Error deleting voice agent:', error);
  }
};

onMounted(() => {
  fetchVoiceAgents();
});
</script>

<template>
  <PageLayout
    :header-title="$t('AI_VOICE.VOICE_AGENTS.HEADER')"
    :button-label="$t('AI_VOICE.VOICE_AGENTS.ADD_NEW')"
    :button-policy="['administrator']"
    :show-pagination-footer="false"
    :is-fetching="isFetching"
    :is-empty="!voiceAgents.length"
    @click="handleCreate"
  >
    <template #emptyState>
      <VoiceAgentEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <VoiceAgentCard
          v-for="agent in voiceAgents"
          :id="agent.id"
          :key="agent.id"
          :name="agent.name"
          :description="agent.description"
          :language="agent.language"
          :voice-price="agent.voice_price"
          :active="agent.active"
          :created-at="agent.created_at"
          @action="handleAction"
        />
      </div>
    </template>
  </PageLayout>

  <!-- Delete confirmation dialog -->
  <div
    v-if="showDeleteDialog"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click="showDeleteDialog = false"
  >
    <div class="bg-n-solid-2 rounded-lg p-6 max-w-md mx-4" @click.stop>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ $t('AI_VOICE.VOICE_AGENTS.DELETE.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{
          $t('AI_VOICE.VOICE_AGENTS.DELETE.DESCRIPTION', {
            name: selectedAgent?.name,
          })
        }}
      </p>
      <div class="flex justify-end gap-2">
        <Button color="slate" size="sm" @click="showDeleteDialog = false">
          {{ $t('AI_VOICE.CANCEL') }}
        </Button>
        <Button color="ruby" size="sm" @click="confirmDelete">
          {{ $t('AI_VOICE.DELETE') }}
        </Button>
      </div>
    </div>
  </div>

  <!-- Create/Edit Voice Agent Dialog -->
  <CreateVoiceAgentDialog
    v-if="showCreateDialog"
    ref="createVoiceAgentDialog"
    :type="dialogType"
    :selected-voice-agent="selectedAgent"
    @close="handleCreateClose"
    @created="handleVoiceAgentCreated"
  />
</template>
